import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import * as storageService from '../services/storageService';
import { Hospital } from '../types';
import {
    BarChart3, ArrowLeft, Clock, Activity, Zap, Building,
    TrendingUp, Users, Timer, Loader2, RefreshCw, ChevronDown,
    Stethoscope, HeartPulse, Filter, Search, X, Check, Download, FileSpreadsheet,
    Calendar, CalendarDays, CheckSquare, Square, FileText, Minus
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';

import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend,
    RadialBarChart, RadialBar
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useHospitalSync } from '../hooks/useHospitalSync';
import { exportBeautifulPDF } from '../utils/exportBeautifulPDF';
import CloudflareMetrics from '../components/CloudflareMetrics';
import { AdminNavbarUser } from '../components/AdminNavbarUser';
import AdAnalyticsDashboard from '../components/AdAnalyticsDashboard';

// ─── Types ──────────────────────────────────────────────────────
interface AnalyticsData {
    kpis: {
        totalSurgeries: number;
        surgeriesToday: number;
        surgeriesInProgress: number;
        avgTotalMinutes: number;
        fastestSurgeryMinutes: number;
        fastestProcedureName: string;
        busiestRoom: string;
        busiestRoomCount: number;
    };
    stationAverages: {
        station: string;
        avgMinutes: number;
        count: number;
        minMinutes: number;
        maxMinutes: number;
    }[];
    procedureAverages: {
        procedure: string;
        avgMinutes: number;
        count: number;
        minMinutes: number;
        maxMinutes: number;
    }[];
    roomUtilization: {
        room: string;
        roomId: string;
        surgeries: number;
    }[];
    recentSurgeries: {
        id: string;
        procedure: string;
        doctor: string;
        room: string;
        status: string;
        admissionTime: string;
        totalMinutes: number;
        stationBreakdown: {
            station: string;
            startTime: string;
            durationMin: number | null;
        }[];
    }[];
    generatedAt: string;
    procedureGroups: Record<string, string[]>;
    dateRange: { startDate: string; endDate: string } | null;
}

// ─── Helpers ─────────────────────────────────────────────────────
const STATION_LABELS: Record<string, string> = {
    WAITING: 'Espera',
    PREPARATION: 'Preparación',
    ROOM_ENTRY: 'Ingreso a Qx',
    ANESTHESIA: 'Anestesia',
    SURGERY: 'Cirugía',
    RECOVERY: 'Recuperación',
};

const STATUS_LABELS: Record<string, string> = {
    WAITING: 'En Espera',
    PREPARATION: 'Preparación',
    ROOM_ENTRY: 'Ingreso a Qx',
    ANESTHESIA: 'Anestesia',
    SURGERY: 'En Cirugía',
    RECOVERY: 'Recuperación',
    EXIT: 'Salida',
};

const formatMinutes = (mins: number) => {
    if (mins === 0) return '—';
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const formatTime = (iso: string) => {
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    } catch { return '—'; }
};

const formatDate = (iso: string) => {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '—'; }
};

// ─── Custom Tooltip ──────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white/95 backdrop-blur-xl px-4 py-3 rounded-2xl shadow-lg border border-slate-100 text-sm">
                <p className="font-bold text-slate-800 mb-1">{label}</p>
                {payload.map((entry: any, i: number) => (
                    <p key={i} className="text-slate-500">
                        <span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: entry.color }}></span>
                        {formatMinutes(entry.value)}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

// ─── Main Component ──────────────────────────────────────────────
const HospitalDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { hospitalId: urlHospitalId } = useParams<{ hospitalId: string }>();
    const authData = storageService.getAuthData();
    const hospitalId = urlHospitalId || authData?.hospitalId;
    const canSeeAdminPanel = !authData?.role || ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN', 'FINANCIERO', 'MARKETING'].includes(authData.role) || ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN', 'FINANCIERO', 'MARKETING'].includes(authData?.staffRole as string);

    useEffect(() => {
        if (!authData && !hospitalId) { navigate('/login'); return; }
        const isImpersonating = window.location.search.includes('impersonate=true');
        if (!isImpersonating && (hospitalId === 'ADMIN' || authData?.role === 'SUPER_ADMIN')) {
            navigate('/admin/dashboard');
            return;
        }
        if (!canSeeAdminPanel) {
            navigate('/staff');
            return;
        }
    }, [hospitalId, authData, navigate]);

    const [expandedSurgery, setExpandedSurgery] = useState<string | null>(null);
    const [selectedProcedures, setSelectedProcedures] = useState<Set<string>>(new Set());
    const [selectedDoctors, setSelectedDoctors] = useState<Set<string>>(new Set());
    const [selectedRooms, setSelectedRooms] = useState<Set<string>>(new Set());
    const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());
    const [filterInitialized, setFilterInitialized] = useState(false);
    const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
        start: '',
        end: '',
    });
    const [selectedPatients, setSelectedPatients] = useState<Set<string>>(new Set());
    const [showExportMenu, setShowExportMenu] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    const { data: hospital } = useHospitalSync(hospitalId);
    
    const { 
        data: analytics, 
        isLoading: loading, 
        isRefetching: refreshing, 
        refetch 
    } = useQuery({
        queryKey: ['analytics', hospitalId, dateRange.start, dateRange.end],
        queryFn: () => storageService.getHospitalAnalytics(hospitalId!, dateRange.start || undefined, dateRange.end || undefined),
        enabled: !!hospitalId,
    });

    // Reset column filters when analytics object actually changes
    useEffect(() => {
        if (analytics) {
            setFilterInitialized(false);
        }
    }, [analytics]);

    const primaryColor = hospital?.branding?.primaryColor || '#5B65DC';

    const CHART_COLORS = useMemo(() => [
        primaryColor,
        primaryColor + 'BF', // 75%
        primaryColor + '8C', // 55%
        primaryColor + '66', // 40%
        primaryColor + '4D', // 30%
        primaryColor + '38', // 22%
        primaryColor + '26', // 15%
    ], [primaryColor]);

    const STATION_COLORS: Record<string, string> = useMemo(() => ({
        WAITING: primaryColor + '40',
        PREPARATION: primaryColor + '66',
        ROOM_ENTRY: primaryColor + '80',
        ANESTHESIA: primaryColor + '99',
        SURGERY: primaryColor,
        RECOVERY: primaryColor + 'BF',
    }), [primaryColor]);

    const handleRefresh = async () => {
        refetch();
    };

    // When date range changes, reload data
    const applyDateRange = (start: string, end: string) => {
        setDateRange({ start, end });
    };

    // Initialize all filters with everything selected when analytics loads
    useEffect(() => {
        if (analytics && !filterInitialized) {
            setSelectedProcedures(new Set(analytics.procedureAverages.map(p => p.procedure)));
            const doctors = new Set(analytics.recentSurgeries.map(s => s.doctor));
            const rooms = new Set(analytics.recentSurgeries.map(s => s.room));
            const statuses = new Set(analytics.recentSurgeries.map(s => s.status));
            setSelectedDoctors(doctors);
            setSelectedRooms(rooms);
            setSelectedStatuses(statuses);
            setFilterInitialized(true);
        }
    }, [analytics, filterInitialized]);

    // All unique values per column
    const allProcedures = useMemo(() => {
        if (!analytics) return [];
        return analytics.procedureAverages.map(p => p.procedure);
    }, [analytics]);

    const allDoctors = useMemo(() => {
        if (!analytics) return [];
        return [...new Set(analytics.recentSurgeries.map(s => s.doctor))].sort();
    }, [analytics]);

    const allRooms = useMemo(() => {
        if (!analytics) return [];
        return [...new Set(analytics.recentSurgeries.map(s => s.room))].sort();
    }, [analytics]);

    const allStatuses = useMemo(() => {
        if (!analytics) return [];
        return [...new Set(analytics.recentSurgeries.map(s => s.status))];
    }, [analytics]);

    const isProcFiltered = selectedProcedures.size > 0 && selectedProcedures.size < allProcedures.length;
    const isDocFiltered = selectedDoctors.size > 0 && selectedDoctors.size < allDoctors.length;
    const isRoomFiltered = selectedRooms.size > 0 && selectedRooms.size < allRooms.length;
    const isStatusFiltered = selectedStatuses.size > 0 && selectedStatuses.size < allStatuses.length;
    const isFiltered = isProcFiltered || isDocFiltered || isRoomFiltered || isStatusFiltered;
    const activeFilterCount = [isProcFiltered, isDocFiltered, isRoomFiltered, isStatusFiltered].filter(Boolean).length;

    // Filtered recent surgeries (all column filters combined)
    const filteredRecentSurgeries = useMemo(() => {
        if (!analytics) return [];
        return analytics.recentSurgeries.filter(s => {
            if (isProcFiltered && !selectedProcedures.has(s.procedure)) return false;
            if (isDocFiltered && !selectedDoctors.has(s.doctor)) return false;
            if (isRoomFiltered && !selectedRooms.has(s.room)) return false;
            if (isStatusFiltered && !selectedStatuses.has(s.status)) return false;
            return true;
        });
    }, [analytics, selectedProcedures, selectedDoctors, selectedRooms, selectedStatuses, isProcFiltered, isDocFiltered, isRoomFiltered, isStatusFiltered]);

    // Filtered KPIs (recalculated when any filter active)
    const filteredKpis = useMemo(() => {
        if (!analytics) return null;
        if (!isFiltered) return analytics.kpis;

        const filtered = filteredRecentSurgeries;
        const totalMins = filtered.reduce((s, r) => s + r.totalMinutes, 0);
        const fastest = filtered.length > 0 ? Math.min(...filtered.map(r => r.totalMinutes).filter(t => t > 0)) : 0;
        const fastestEntry = filtered.find(r => r.totalMinutes === fastest);

        return {
            ...analytics.kpis,
            totalSurgeries: filtered.length,
            avgTotalMinutes: filtered.length > 0 ? Math.round(totalMins / filtered.length) : 0,
            fastestSurgeryMinutes: fastest === Infinity ? 0 : fastest,
            fastestProcedureName: fastestEntry?.procedure || 'N/A',
        };
    }, [analytics, filteredRecentSurgeries, isFiltered]);

    // Chart data transforms
    const stationChartData = useMemo(() => {
        if (!analytics) return [];
        return analytics.stationAverages
            .map(s => ({
                name: STATION_LABELS[s.station] || s.station,
                promedio: s.avgMinutes,
                min: s.minMinutes,
                max: s.maxMinutes,
                fill: STATION_COLORS[s.station] || primaryColor,
            }));
    }, [analytics, primaryColor]);

    const procedureChartData = useMemo(() => {
        if (!analytics) return [];
        let data = analytics.procedureAverages;
        if (isProcFiltered) {
            data = data.filter(p => selectedProcedures.has(p.procedure));
        }
        return data.slice(0, 8).map(p => ({
            name: p.procedure.length > 20 ? p.procedure.slice(0, 18) + '…' : p.procedure,
            fullName: p.procedure,
            promedio: p.avgMinutes,
            count: p.count,
        }));
    }, [analytics, selectedProcedures, isProcFiltered]);

    // ─── Patient Selection Helpers ──────────────────────────────────
    const togglePatientSelection = (id: string) => {
        setSelectedPatients(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAllPatients = () => {
        setSelectedPatients(new Set(filteredRecentSurgeries.map(s => s.id)));
    };

    const deselectAllPatients = () => {
        setSelectedPatients(new Set());
    };

    const allVisibleSelected = filteredRecentSurgeries.length > 0 && filteredRecentSurgeries.every(s => selectedPatients.has(s.id));
    const someVisibleSelected = filteredRecentSurgeries.some(s => selectedPatients.has(s.id));

    // Close export menu on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setShowExportMenu(false);
            }
        };
        if (showExportMenu) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showExportMenu]);

    // ─── Export Helpers ──────────────────────────────────────────
    const getExportRows = (patientIds?: string[]) => {
        let surgeries = filteredRecentSurgeries;
        if (patientIds && patientIds.length > 0) {
            const ids = new Set(patientIds);
            surgeries = surgeries.filter(s => ids.has(s.id));
        }
        return surgeries.map(s => ({
            Codigo: s.id,
            Procedimiento: s.procedure,
            Doctor: s.doctor,
            Sala: s.room,
            Estado: STATUS_LABELS[s.status] || s.status,
            Fecha: formatDate(s.admissionTime),
            Hora: formatTime(s.admissionTime),
            'Duracion Total (min)': s.totalMinutes,
            'Registro (min)': s.stationBreakdown.find(st => st.station === 'WAITING')?.durationMin ?? '',
            'Preoperatorio (min)': s.stationBreakdown.find(st => st.station === 'PREPARATION')?.durationMin ?? '',
            'Ingreso a Quirófano (min)': s.stationBreakdown.find(st => st.station === 'ROOM_ENTRY')?.durationMin ?? '',
            'Anestesia (min)': s.stationBreakdown.find(st => st.station === 'ANESTHESIA')?.durationMin ?? '',
            'Cirugia (min)': s.stationBreakdown.find(st => st.station === 'SURGERY')?.durationMin ?? '',
            'Recuperacion (min)': s.stationBreakdown.find(st => st.station === 'RECOVERY')?.durationMin ?? '',
        }));
    };

    // ─── Export Functions ─────────────────────────────────────────
    const exportCSV = (patientIds?: string[]) => {
        if (!analytics || !hospital) return;
        const rows = getExportRows(patientIds);
        if (rows.length === 0) return;

        const headers = Object.keys(rows[0]);
        const csvContent = [
            headers.join(','),
            ...rows.map(r => headers.map(h => {
                const val = String((r as any)[h] ?? '');
                return val.includes(',') ? `"${val}"` : val;
            }).join(','))
        ].join('\n');

        const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const date = new Date().toISOString().split('T')[0];
        const suffix = patientIds && patientIds.length === 1 ? `_${patientIds[0]}` : '';
        link.download = `reporte_cirugias_${hospital.name.replace(/\s+/g, '_')}_${date}${suffix}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    // Export Functions
    const exportXLS = (patientIds?: string[]) => {
        if (!analytics || !hospital) return;
        const rows = getExportRows(patientIds);
        if (rows.length === 0) return;

        const wb = XLSX.utils.book_new();

        // Sheet 1: Patient data
        const ws = XLSX.utils.json_to_sheet(rows);
        // Auto-fit column widths
        const colWidths = Object.keys(rows[0]).map(key => {
            const maxLen = Math.max(key.length, ...rows.map(r => String((r as any)[key] ?? '').length));
            return { wch: Math.min(maxLen + 2, 40) };
        });
        ws['!cols'] = colWidths;
        XLSX.utils.book_append_sheet(wb, ws, 'Pacientes');

        // Sheet 2: KPI summary
        const displayK = filteredKpis || analytics.kpis;
        const kpiRows = [
            { Metrica: 'Cirugías Totales', Valor: displayK.totalSurgeries },
            { Metrica: 'Cirugías Hoy', Valor: displayK.surgeriesToday },
            { Metrica: 'Tiempo Promedio (min)', Valor: displayK.avgTotalMinutes },
            { Metrica: 'Cirugía Más Rápida (min)', Valor: displayK.fastestSurgeryMinutes },
            { Metrica: 'Procedimiento Más Rápido', Valor: displayK.fastestProcedureName },
            { Metrica: 'Quirófano Más Activo', Valor: `${displayK.busiestRoom} (${displayK.busiestRoomCount} cirugías)` },
        ];
        const wsKpi = XLSX.utils.json_to_sheet(kpiRows);
        wsKpi['!cols'] = [{ wch: 28 }, { wch: 30 }];
        XLSX.utils.book_append_sheet(wb, wsKpi, 'Resumen KPIs');

        const date = new Date().toISOString().split('T')[0];
        const suffix = patientIds && patientIds.length === 1 ? `_${patientIds[0]}` : '';
        XLSX.writeFile(wb, `reporte_cirugias_${hospital.name.replace(/\s+/g, '_')}_${date}${suffix}.xlsx`);
    };

    const handleExport = (format: 'csv' | 'xlsx', patientIds?: string[]) => {
        if (format === 'csv') exportCSV(patientIds);
        else exportXLS(patientIds);
        setShowExportMenu(false);
    };
    // End bulk export
    const exportSingleSurgeryCSV = (surgery: any) => {
        // Basic Info Section
        const basicInfoHeaders = ['ID', 'Procedimiento', 'Doctor', 'Sala', 'Ingreso', 'Duración Total (min)'].join(',');
        const cleanProc = typeof surgery.procedure === 'string' ? surgery.procedure.replace(/,/g, ' ') : '';
        const cleanDoctor = typeof surgery.doctor === 'string' ? surgery.doctor.replace(/,/g, ' ') : '';
        const cleanRoom = typeof surgery.room === 'string' ? surgery.room.replace(/,/g, ' ') : '';
        
        let admissionTimeStr = '---';
        if (surgery.admissionTime) {
          try { admissionTimeStr = new Date(surgery.admissionTime).toLocaleString(); } catch(e){}
        }
        
        const basicInfoRow = `"${surgery.id}","${cleanProc}","${cleanDoctor}","${cleanRoom}","${admissionTimeStr}","${surgery.totalMinutes}"`;

        // Logs/Stations Section
        const logHeaders = ['Estación', 'Duración (min)'].join(',');
        const logRows = (surgery.stationBreakdown || [])
          .filter((s: any) => s.durationMin !== null)
          .map((s: any) => {
            const label = STATION_LABELS[s.station] || s.station;
            return `"${label}","${s.durationMin}"`;
          }).join('\n');

        const csvContent = `[Datos de la Cirugía]\n${basicInfoHeaders}\n${basicInfoRow}\n\n[Desglose por Estación]\n${logHeaders}\n${logRows}`;

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const filename = `Reporte_${surgery.id}_${new Date().toISOString().split('T')[0]}.csv`;

        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // end of export functions

    const clearAllFilters = () => {
        setSelectedProcedures(new Set(allProcedures));
        setSelectedDoctors(new Set(allDoctors));
        setSelectedRooms(new Set(allRooms));
        setSelectedStatuses(new Set(allStatuses));
    };

    const roomChartData = useMemo(() => {
        if (!analytics) return [];
        return analytics.roomUtilization.map(r => ({
            name: r.room,
            value: r.surgeries,
        }));
    }, [analytics]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] font-sans flex flex-col">
                <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-slate-200/50 animate-pulse" />
                        <div className="space-y-2">
                           <div className="h-5 w-32 bg-slate-200/50 animate-pulse rounded-md" />
                           <div className="h-3 w-16 bg-slate-200/50 animate-pulse rounded-md" />
                        </div>
                    </div>
                </header>
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 w-full flex-1 space-y-6 sm:space-y-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
                       {[1, 2, 3, 4].map(i => (
                         <div key={i} className="h-[140px] bg-slate-200/40 animate-pulse rounded-2xl sm:rounded-3xl" />
                       ))}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-8">
                       <div className="h-[380px] bg-slate-200/40 animate-pulse rounded-2xl sm:rounded-3xl" />
                       <div className="h-[380px] bg-slate-200/40 animate-pulse rounded-2xl sm:rounded-3xl" />
                    </div>
                </main>
            </div>
        );
    }

    if (!hospital || !analytics) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] font-sans">
                <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center gap-3 sm:gap-4">
                        <button
                            onClick={() => {
                                if (authData?.role === 'GROUP_ADMIN') navigate('/c/' + (authData.groupId) + window.location.search); // O regresar a /admin/dashboard
                                else navigate('/staff/paneladmin' + window.location.search);
                            }}
                            className="p-2 sm:p-2.5 rounded-xl hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        {hospital?.branding?.logoUrl ? (
                            <img src={hospital.branding.logoUrl} alt="Logo" className="h-8 sm:h-10 w-auto object-contain" />
                        ) : (
                            <div className="p-2 rounded-xl" style={{ backgroundColor: `${primaryColor}15` }}>
                                <HeartPulse className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: primaryColor }} />
                            </div>
                        )}
                        <div className="min-w-0">
                            <h1 className="text-base sm:text-lg font-black text-slate-800 tracking-tight truncate">{hospital?.name || 'Analíticas'}</h1>
                            <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest">Analíticas</p>
                        </div>
                    </div>
                </header>
                <div className="flex flex-col items-center justify-center p-6 text-center" style={{ minHeight: 'calc(100vh - 5rem)' }}>
                    <BarChart3 className="w-16 h-16 text-slate-300 mb-4" />
                    <h1 className="text-2xl font-bold text-slate-800 mb-2">Sin datos disponibles</h1>
                    <p className="text-slate-500 mb-6">No hay suficientes datos para generar analíticas aún.</p>
                    <button
                        onClick={() => {
                            if (authData?.role === 'GROUP_ADMIN') navigate('/c/' + (authData.groupId) + window.location.search);
                            else navigate('/staff/paneladmin' + window.location.search);
                        }}
                        className="text-sm font-bold px-6 py-3 rounded-full border border-slate-200 hover:bg-white transition-colors"
                    >
                        ← Volver al Panel
                    </button>
                </div>
            </div>
        );
    }

    const { kpis } = analytics;
    const displayKpis = filteredKpis || kpis;

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans">
            {/* ─── Header ─────────────────────────────────────── */}
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
                    <div className="flex items-center gap-3 sm:gap-4">
                        <button
                            onClick={() => {
                                if (authData?.role === 'GROUP_ADMIN') navigate('/c/' + (authData.groupId) + window.location.search);
                                else navigate((hospitalId ? `/staff/paneladmin/${hospitalId}` : '/staff/paneladmin') + window.location.search);
                            }}
                            className="p-2 sm:p-2.5 rounded-xl hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        {hospital.branding?.logoUrl ? (
                            <img src={hospital.branding.logoUrl} alt="Logo" className="h-8 sm:h-10 w-auto object-contain" />
                        ) : (
                            <div className="p-2 rounded-xl" style={{ backgroundColor: `${primaryColor}15` }}>
                                <HeartPulse className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: primaryColor }} />
                            </div>
                        )}
                        <div className="min-w-0">
                            <h1 className="text-base sm:text-lg font-black text-slate-800 tracking-tight truncate">{hospital.name}</h1>
                            <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest">Analíticas</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <AdminNavbarUser primaryColor={primaryColor} className="hidden md:flex mr-4" />
                        <DateRangePicker
                            dateRange={dateRange}
                            onApply={applyDateRange}
                            primaryColor={primaryColor}
                        />
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="flex items-center gap-2 text-xs font-bold px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all active:scale-95 disabled:opacity-50"
                            style={{ color: primaryColor }}
                        >
                            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">Actualizar</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* ─── Analytics Content ──────────────────────────────── */}
            {true && (
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6 sm:space-y-8">

                    {/* ─── KPI Cards ───────────────────────────────── */}
                    <motion.section
                        initial="hidden" animate="visible"
                        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.08 } } }}
                        className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5"
                    >
                        {[
                            {
                                label: 'Cirugías Totales',
                                value: displayKpis.totalSurgeries,
                                sub: isFiltered ? `${activeFilterCount} filtro${activeFilterCount > 1 ? 's' : ''} activo${activeFilterCount > 1 ? 's' : ''}` : `${displayKpis.surgeriesToday} hoy`,
                                icon: Activity,
                                color: primaryColor,
                                bg: `${primaryColor}12`,
                            },
                            {
                                label: 'Tiempo Promedio',
                                value: formatMinutes(displayKpis.avgTotalMinutes),
                                sub: 'por procedimiento',
                                icon: Clock,
                                color: primaryColor + 'CC',
                                bg: `${primaryColor}10`,
                            },
                            {
                                label: 'Cirugía Más Rápida',
                                value: formatMinutes(displayKpis.fastestSurgeryMinutes),
                                sub: displayKpis.fastestProcedureName || 'N/A',
                                icon: Zap,
                                color: primaryColor + '99',
                                bg: `${primaryColor}0D`,
                            },
                            {
                                label: 'Quirófano Más Activo',
                                value: displayKpis.busiestRoom,
                                sub: `${displayKpis.busiestRoomCount} cirugías`,
                                icon: Building,
                                color: primaryColor + 'B3',
                                bg: `${primaryColor}0A`,
                            }
                        ].map((kpi, i) => (
                            <motion.div
                                key={i}
                                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                                className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.06)] transition-shadow"
                            >
                                <div className="flex items-start justify-between mb-3 sm:mb-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center" style={{ backgroundColor: kpi.bg }}>
                                        <kpi.icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: kpi.color }} />
                                    </div>
                                    {i === 0 && !isFiltered && kpis.surgeriesInProgress > 0 && (
                                        <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-[9px] sm:text-[10px] font-bold text-emerald-600">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                            {kpis.surgeriesInProgress} activa{kpis.surgeriesInProgress > 1 ? 's' : ''}
                                        </span>
                                    )}
                                    {i === 0 && isFiltered && (
                                        <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-[9px] sm:text-[10px] font-bold text-indigo-600">
                                            <Filter className="w-3 h-3" />
                                            Filtrado
                                        </span>
                                    )}
                                </div>
                                <div className="text-xl sm:text-2xl lg:text-3xl font-black text-slate-800 tracking-tight mb-0.5 sm:mb-1 truncate">{kpi.value}</div>
                                <div className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider truncate">{kpi.label}</div>
                                <div className="text-[10px] sm:text-xs text-slate-400 mt-0.5 truncate">{kpi.sub}</div>
                            </motion.div>
                        ))}
                    </motion.section>

                    {/* ─── Charts Grid ─────────────────────────────── */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-8">

                        {/* Time per Station */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)]"
                        >
                            <div className="flex items-center gap-3 mb-6 sm:mb-8">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}12` }}>
                                    <Timer className="w-5 h-5" style={{ color: primaryColor }} />
                                </div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight">Tiempo por Estación</h3>
                                    <p className="text-[10px] sm:text-xs text-slate-400 font-medium">Promedio de minutos en cada fase</p>
                                </div>
                            </div>
                            {stationChartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={stationChartData} layout="vertical" margin={{ top: 0, right: 30, left: 20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                                        <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}m`} />
                                        <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fontWeight: 600, fill: '#475569' }} axisLine={false} tickLine={false} width={100} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="promedio" radius={[0, 8, 8, 0]} barSize={24}>
                                            {stationChartData.map((entry, index) => (
                                                <Cell key={index} fill={entry.fill} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-[280px] flex items-center justify-center text-slate-300">
                                    <p className="text-sm font-medium">Sin datos suficientes</p>
                                </div>
                            )}
                        </motion.div>

                        {/* Room Utilization */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)]"
                        >
                            <div className="flex items-center gap-3 mb-6 sm:mb-8">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                                    <Building className="w-5 h-5 text-violet-500" />
                                </div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight">Utilización de Quirófanos</h3>
                                    <p className="text-[10px] sm:text-xs text-slate-400 font-medium">Distribución de cirugías por sala</p>
                                </div>
                            </div>
                            {roomChartData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie
                                            data={roomChartData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={65}
                                            outerRadius={110}
                                            paddingAngle={4}
                                            dataKey="value"
                                            strokeWidth={0}
                                        >
                                            {roomChartData.map((_, index) => (
                                                <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value: any) => [`${value} cirugías`, '']} />
                                        <Legend
                                            verticalAlign="bottom"
                                            iconType="circle"
                                            iconSize={8}
                                            formatter={(value: string) => <span className="text-xs font-semibold text-slate-600">{value}</span>}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-[280px] flex items-center justify-center text-slate-300">
                                    <p className="text-sm font-medium">Sin datos suficientes</p>
                                </div>
                            )}
                        </motion.div>

                        {/* Time per Procedure */}
                        {procedureChartData.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                                className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] lg:col-span-2"
                            >
                                <div className="flex items-center justify-between mb-6 sm:mb-8">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}12` }}>
                                            <Stethoscope className="w-5 h-5" style={{ color: primaryColor }} />
                                        </div>
                                        <div>
                                            <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight">Tiempo por Tipo de Cirugía</h3>
                                            <p className="text-[10px] sm:text-xs text-slate-400 font-medium">Duración promedio agrupada por procedimiento</p>
                                        </div>
                                    </div>
                                    <ColumnFilterDropdown
                                        label="Procedimiento"
                                        items={allProcedures}
                                        selected={selectedProcedures}
                                        onChange={setSelectedProcedures}
                                        primaryColor={primaryColor}
                                    />
                                </div>
                                <ResponsiveContainer width="100%" height={Math.max(200, procedureChartData.length * 50)}>
                                    <BarChart data={procedureChartData} layout="vertical" margin={{ top: 0, right: 40, left: 10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                                        <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}m`} />
                                        <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fontWeight: 600, fill: '#475569' }} axisLine={false} tickLine={false} width={160} />
                                        <Tooltip
                                            content={({ active, payload }: any) => {
                                                if (active && payload && payload.length) {
                                                    const d = payload[0].payload;
                                                    return (
                                                        <div className="bg-white/95 backdrop-blur-xl px-4 py-3 rounded-2xl shadow-lg border border-slate-100 text-sm">
                                                            <p className="font-bold text-slate-800 mb-1">{d.fullName}</p>
                                                            <p className="text-slate-500">{formatMinutes(d.promedio)} promedio</p>
                                                            <p className="text-slate-400 text-xs">{d.count} cirugía{d.count > 1 ? 's' : ''}</p>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Bar dataKey="promedio" radius={[0, 8, 8, 0]} barSize={20} fill={primaryColor}>
                                            {procedureChartData.map((_, i) => (
                                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} opacity={0.85} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </motion.div>
                        )}
                    </div>

                    {/* ─── Recent Surgeries Table ─────────────────── */}
                    {analytics.recentSurgeries.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                            className="bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] overflow-hidden"
                        >
                            <div className="p-5 sm:p-8 pb-0">
                                <div className="flex items-center justify-between mb-4 sm:mb-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}12` }}>
                                            <TrendingUp className="w-5 h-5" style={{ color: primaryColor }} />
                                        </div>
                                        <div>
                                            <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight">Actividad Reciente</h3>
                                            <p className="text-[10px] sm:text-xs text-slate-400 font-medium">
                                                {isFiltered
                                                    ? `${filteredRecentSurgeries.length} de ${analytics.recentSurgeries.length} cirugías`
                                                    : 'Últimas cirugías con desglose de tiempos'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {isFiltered && (
                                            <button
                                                onClick={clearAllFilters}
                                                className="flex items-center gap-1 text-[10px] font-bold px-2.5 py-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
                                            >
                                                <X className="w-3 h-3" />
                                                Limpiar filtros
                                            </button>
                                        )}
                                        {/* Split Export Dropdown */}
                                        <div className="relative" ref={exportMenuRef}>
                                            <div className="flex items-center rounded-xl border overflow-hidden" style={{ borderColor: primaryColor + '30' }}>
                                                <button
                                                    onClick={() => handleExport('csv', selectedPatients.size > 0 ? [...selectedPatients] : undefined)}
                                                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 transition-all active:scale-95 hover:opacity-80"
                                                    style={{ backgroundColor: primaryColor + '0A', color: primaryColor }}
                                                >
                                                    <FileSpreadsheet className="w-3.5 h-3.5" />
                                                    <span className="hidden sm:inline">
                                                        {selectedPatients.size > 0 ? `Exportar ${selectedPatients.size}` : 'Exportar'}
                                                    </span>
                                                </button>
                                                <div className="w-px h-6 opacity-30" style={{ backgroundColor: primaryColor }} />
                                                <button
                                                    onClick={() => setShowExportMenu(v => !v)}
                                                    className="flex items-center px-2 py-2 transition-all hover:opacity-80"
                                                    style={{ backgroundColor: primaryColor + '0A', color: primaryColor }}
                                                >
                                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
                                                </button>
                                            </div>
                                            <AnimatePresence>
                                                {showExportMenu && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: -6, scale: 0.95 }}
                                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                                        exit={{ opacity: 0, y: -6, scale: 0.95 }}
                                                        transition={{ duration: 0.15 }}
                                                        className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl border border-slate-100 shadow-xl z-50 overflow-hidden"
                                                    >
                                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-4 pt-3 pb-1">Formato</p>
                                                        <button
                                                            onClick={() => handleExport('csv', selectedPatients.size > 0 ? [...selectedPatients] : undefined)}
                                                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
                                                        >
                                                            <FileText className="w-4 h-4 text-emerald-500" />
                                                            <div>
                                                                <p className="text-sm font-semibold text-slate-700">CSV</p>
                                                                <p className="text-[10px] text-slate-400">Compatible con Excel y Google Sheets</p>
                                                            </div>
                                                        </button>
                                                        <button
                                                            onClick={() => handleExport('xlsx', selectedPatients.size > 0 ? [...selectedPatients] : undefined)}
                                                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
                                                        >
                                                            <FileSpreadsheet className="w-4 h-4 text-blue-500" />
                                                            <div>
                                                                <p className="text-sm font-semibold text-slate-700">Excel (.xlsx)</p>
                                                                <p className="text-[10px] text-slate-400">Con KPIs en hoja separada</p>
                                                            </div>
                                                        </button>
                                                        {selectedPatients.size > 0 && (
                                                            <>
                                                                <div className="border-t border-slate-100 mx-4 my-1" />
                                                                <p className="text-[10px] text-slate-400 px-4 py-1.5 flex items-center gap-1">
                                                                    <Check className="w-3 h-3 text-emerald-500" />
                                                                    {selectedPatients.size} paciente{selectedPatients.size > 1 ? 's' : ''} seleccionado{selectedPatients.size > 1 ? 's' : ''}
                                                                </p>
                                                            </>
                                                        )}
                                                        <div className="border-t border-slate-100 mx-4 my-1" />
                                                        <button
                                                            onClick={() => { handleExport('csv'); handleExport('xlsx'); }}
                                                            className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left mb-1"
                                                        >
                                                            <Download className="w-4 h-4 text-slate-400" />
                                                            <div>
                                                                <p className="text-sm font-semibold text-slate-600">Ambos formatos</p>
                                                                <p className="text-[10px] text-slate-400">Descarga CSV y Excel juntos</p>
                                                            </div>
                                                        </button>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Mobile: Card layout */}
                            <div className="sm:hidden px-4 pb-4 space-y-3">
                                {filteredRecentSurgeries.map((surgery) => (
                                    <div key={surgery.id} className="border border-slate-100 rounded-2xl overflow-hidden">
                                        <button
                                            onClick={() => setExpandedSurgery(expandedSurgery === surgery.id ? null : surgery.id)}
                                            className="w-full p-4 flex items-center justify-between text-left"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-mono font-bold text-sm text-slate-800">{surgery.id}</span>
                                                    <StatusBadge status={surgery.status} />
                                                </div>
                                                <p className="text-xs text-slate-500 truncate">{surgery.procedure}</p>
                                                <p className="text-[10px] text-slate-400">{formatDate(surgery.admissionTime)} • {formatTime(surgery.admissionTime)}</p>
                                            </div>
                                            <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                                <span className="text-sm font-bold" style={{ color: primaryColor }}>{formatMinutes(surgery.totalMinutes)}</span>
                                                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedSurgery === surgery.id ? 'rotate-180' : ''}`} />
                                            </div>
                                        </button>
                                        <AnimatePresence>
                                            {expandedSurgery === surgery.id && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="overflow-hidden border-t border-slate-50"
                                                >
                                                    <div className="p-4 pt-3 bg-slate-50/50 space-y-2">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <p className="text-xs text-slate-500"><span className="font-semibold">Doctor:</span> {surgery.doctor}</p>
                                                                <p className="text-xs text-slate-500"><span className="font-semibold">Sala:</span> {surgery.room}</p>
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); exportBeautifulPDF(surgery); }}
                                                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 bg-indigo-50/50 hover:bg-indigo-100 text-[10px] font-bold uppercase tracking-widest transition-colors shadow-sm`}
                                                                >
                                                                    <FileSpreadsheet className="w-3 h-3" />
                                                                    PDF
                                                                </button>
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); exportSingleSurgeryCSV(surgery); }}
                                                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 text-[10px] font-bold uppercase tracking-widest transition-colors shadow-sm`}
                                                                >
                                                                    <FileSpreadsheet className="w-3 h-3" />
                                                                    CSV
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="mt-2 space-y-1.5 pt-2 border-t border-slate-200/60">
                                                            {surgery.stationBreakdown.filter(s => s.durationMin !== null).map((s, i) => (
                                                                <div key={i} className="flex items-center justify-between text-xs">
                                                                    <span className="text-slate-500">{STATION_LABELS[s.station] || s.station}</span>
                                                                    <span className="font-mono font-semibold text-slate-700">{formatMinutes(s.durationMin!)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop: Table layout */}
                            <div className="hidden sm:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-slate-100">
                                            <th className="px-5 py-4 w-12">
                                                <button
                                                    onClick={allVisibleSelected ? deselectAllPatients : selectAllPatients}
                                                    className="flex items-center justify-center w-5 h-5 transition-all hover:scale-110"
                                                >
                                                    {allVisibleSelected
                                                        ? <CheckSquare className="w-5 h-5" style={{ color: primaryColor }} />
                                                        : someVisibleSelected
                                                            ? <div className="w-4 h-4 rounded border-2 flex items-center justify-center" style={{ borderColor: primaryColor, color: primaryColor }}><Minus className="w-3 h-3" /></div>
                                                            : <Square className="w-5 h-5 text-slate-300" />
                                                    }
                                                </button>
                                            </th>
                                            <th className="text-left px-3 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Código</th>
                                            <th className="text-left px-4 py-2">
                                                <ColumnFilterDropdown
                                                    label="Procedimiento"
                                                    items={allProcedures}
                                                    selected={selectedProcedures}
                                                    onChange={setSelectedProcedures}
                                                    primaryColor={primaryColor}
                                                />
                                            </th>
                                            <th className="text-left px-4 py-2">
                                                <ColumnFilterDropdown
                                                    label="Doctor"
                                                    items={allDoctors}
                                                    selected={selectedDoctors}
                                                    onChange={setSelectedDoctors}
                                                    primaryColor={primaryColor}
                                                />
                                            </th>
                                            <th className="text-left px-4 py-2">
                                                <ColumnFilterDropdown
                                                    label="Sala"
                                                    items={allRooms}
                                                    selected={selectedRooms}
                                                    onChange={setSelectedRooms}
                                                    primaryColor={primaryColor}
                                                    displayMap={analytics?.roomUtilization.reduce((m, r) => ({ ...m, [r.roomId]: r.room }), {} as Record<string, string>)}
                                                />
                                            </th>
                                            <th className="text-left px-4 py-2">
                                                <ColumnFilterDropdown
                                                    label="Estado"
                                                    items={allStatuses}
                                                    selected={selectedStatuses}
                                                    onChange={setSelectedStatuses}
                                                    primaryColor={primaryColor}
                                                    displayMap={STATUS_LABELS}
                                                />
                                            </th>
                                            <th className="text-left px-4 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Hora</th>
                                            <th className="text-right px-8 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Duración</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRecentSurgeries.map((surgery, idx) => {
                                            const isActive = surgery.status !== 'EXIT';
                                            return (
                                            <React.Fragment key={surgery.id}>
                                                <tr
                                                    className={`border-b transition-colors cursor-pointer group ${isActive
                                                        ? 'bg-emerald-50/60 border-l-4 border-l-emerald-500 border-b-emerald-100 hover:bg-emerald-50'
                                                        : selectedPatients.has(surgery.id)
                                                            ? 'bg-indigo-50/50 border-b-slate-50'
                                                            : idx % 2 === 1 ? 'bg-slate-50/30 hover:bg-slate-100/40 border-b-slate-50' : 'hover:bg-slate-50/60 border-b-slate-50'
                                                        }`}
                                                    onClick={() => setExpandedSurgery(expandedSurgery === surgery.id ? null : surgery.id)}
                                                >
                                                    <td className="px-5 py-4" onClick={e => { e.stopPropagation(); togglePatientSelection(surgery.id); }}>
                                                        <button className="flex items-center justify-center w-5 h-5 transition-all hover:scale-110">
                                                            {selectedPatients.has(surgery.id)
                                                                ? <CheckSquare className="w-5 h-5" style={{ color: primaryColor }} />
                                                                : <Square className="w-5 h-5 text-slate-300 group-hover:text-slate-400" />
                                                            }
                                                        </button>
                                                    </td>
                                                    <td className="px-3 py-4">
                                                        <span className="font-mono font-bold text-sm text-slate-800">{surgery.id}</span>
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-slate-600 font-medium max-w-[200px] truncate">{surgery.procedure}</td>
                                                    <td className="px-4 py-4 text-sm text-slate-500">{surgery.doctor}</td>
                                                    <td className="px-4 py-4 text-sm text-slate-500">{surgery.room}</td>
                                                    <td className="px-4 py-4"><StatusBadge status={surgery.status} /></td>
                                                    <td className="px-4 py-4 text-xs text-slate-400 font-mono">
                                                        {formatDate(surgery.admissionTime)}<br />{formatTime(surgery.admissionTime)}
                                                    </td>
                                                    <td className="px-8 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <span className="text-sm font-bold" style={{ color: primaryColor }}>{formatMinutes(surgery.totalMinutes)}</span>
                                                            <button
                                                                title="Descargar paciente (XLS)"
                                                                onClick={e => { e.stopPropagation(); exportXLS([surgery.id]); }}
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:bg-white/80"
                                                                style={{ color: primaryColor }}
                                                            >
                                                                <Download className="w-3.5 h-3.5" />
                                                            </button>
                                                            <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform ${expandedSurgery === surgery.id ? 'rotate-180' : ''}`} />
                                                        </div>
                                                    </td>
                                                </tr>
                                                <AnimatePresence>
                                                    {expandedSurgery === surgery.id && (
                                                        <tr>
                                                            <td colSpan={8} className="px-8 py-0">
                                                                <motion.div
                                                                    initial={{ height: 0, opacity: 0 }}
                                                                    animate={{ height: 'auto', opacity: 1 }}
                                                                    exit={{ height: 0, opacity: 0 }}
                                                                    className="overflow-hidden"
                                                                >
                                                                    <div className="py-4 flex flex-col gap-4">
                                                                        <div className="flex items-center justify-between px-2">
                                                                            <h4 className="text-[10px] items-center gap-1.5 flex font-bold text-slate-500 uppercase tracking-widest">
                                                                                <Clock className="w-3 h-3" /> Tiempos por estación
                                                                            </h4>
                                                                            <div className="flex items-center gap-2">
                                                                                <button
                                                                                    onClick={(e) => { e.stopPropagation(); exportBeautifulPDF(surgery); }}
                                                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-colors hover:bg-indigo-50 text-indigo-600 border-indigo-200 bg-indigo-50/30`}
                                                                                >
                                                                                    <FileSpreadsheet className="w-3 h-3" />
                                                                                    PDF
                                                                                </button>
                                                                                <button
                                                                                    onClick={(e) => { e.stopPropagation(); exportSingleSurgeryCSV(surgery); }}
                                                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-widest transition-colors hover:bg-slate-50 text-slate-600 border-slate-200`}
                                                                                >
                                                                                    <FileSpreadsheet className="w-3 h-3" />
                                                                                    Exportar CSV
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-3">
                                                                            {surgery.stationBreakdown.filter((s: any) => s.durationMin !== null).map((s: any, i: number) => (
                                                                            <div
                                                                                key={i}
                                                                                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-100 bg-slate-50/50"
                                                                            >
                                                                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: STATION_COLORS[s.station] || '#94A3B8' }}></div>
                                                                                <span className="text-xs font-medium text-slate-500">{STATION_LABELS[s.station] || s.station}</span>
                                                                                <span className="text-xs font-bold text-slate-700 font-mono">{formatMinutes(s.durationMin!)}</span>
                                                                            </div>
                                                                        ))}
                                                                        </div>
                                                                    </div>
                                                                </motion.div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </AnimatePresence>
                                            </React.Fragment>
                                        );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </motion.div>
                    )}

                    {/* ─── Floating Selection Bar ──────────────────── */}
                    <AnimatePresence>
                        {selectedPatients.size > 0 && (
                            <motion.div
                                initial={{ y: 100, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 100, opacity: 0 }}
                                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl border border-white/20"
                                style={{ backgroundColor: primaryColor, backdropFilter: 'blur(16px)' }}
                            >
                                <div className="flex items-center gap-2">
                                    <CheckSquare className="w-4 h-4 text-white/80" />
                                    <span className="text-sm font-bold text-white">
                                        {selectedPatients.size} paciente{selectedPatients.size > 1 ? 's' : ''} seleccionado{selectedPatients.size > 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className="w-px h-5 bg-white/30" />
                                <button
                                    onClick={() => handleExport('csv', [...selectedPatients])}
                                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-all active:scale-95"
                                >
                                    <FileText className="w-3.5 h-3.5" />
                                    CSV
                                </button>
                                <button
                                    onClick={() => handleExport('xlsx', [...selectedPatients])}
                                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-all active:scale-95"
                                >
                                    <FileSpreadsheet className="w-3.5 h-3.5" />
                                    Excel
                                </button>
                                <div className="w-px h-5 bg-white/30" />
                                <button
                                    onClick={deselectAllPatients}
                                    className="p-1 rounded-lg hover:bg-white/20 text-white/70 hover:text-white transition-all"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* ─── Ad Performance Analytics ──────────────── */}
                    <div className="mt-12 sm:mt-16">
                         <AdAnalyticsDashboard
                             theme="light"
                             hospitalId={hospitalId}
                             hospitals={hospital ? [hospital] : []}
                             adAssets={hospital?.preferences?.adAssets || []}
                         />
                    </div>

                    {/* ─── Cloudflare Analytics ─────────────────────── */}
                    <div className="mt-12 sm:mt-16">
                         <CloudflareMetrics theme="light" />
                    </div>

                    {/* ─── Footer ─────────────────────────────────── */}
                    <div className="text-center py-6 sm:py-8">
                        <p className="text-[10px] font-bold text-slate-300 uppercase tracking-[0.2em]">
                            Hospital Status Analytics • Generado {formatDate(analytics.generatedAt)} a las {formatTime(analytics.generatedAt)}
                        </p>
                    </div>
                </main>
            )}
        </div>
    );
};

// ─── Sub-Components ──────────────────────────────────────────────

// Date Range Picker with presets
const DateRangePicker: React.FC<{
    dateRange: { start: string; end: string };
    onApply: (start: string, end: string) => void;
    primaryColor: string;
}> = ({ dateRange, onApply, primaryColor }) => {
    const [open, setOpen] = useState(false);
    const [tempStart, setTempStart] = useState(dateRange.start);
    const [tempEnd, setTempEnd] = useState(dateRange.end);
    const ref = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    useEffect(() => {
        setTempStart(dateRange.start);
        setTempEnd(dateRange.end);
    }, [dateRange]);

    const today = new Date();
    const fmt = (d: Date) => d.toISOString().split('T')[0];
    const hasDateFilter = dateRange.start && dateRange.end;

    const presets = [
        { label: 'Hoy', start: fmt(today), end: fmt(today) },
        { label: '7d', start: fmt(new Date(today.getTime() - 7 * 86400000)), end: fmt(today) },
        { label: '30d', start: fmt(new Date(today.getTime() - 30 * 86400000)), end: fmt(today) },
        {
            label: 'Mes',
            start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)),
            end: fmt(today),
        },
    ];

    const applyPreset = (start: string, end: string) => {
        onApply(start, end);
        setOpen(false);
    };

    const applyCustom = () => {
        if (tempStart && tempEnd) {
            onApply(tempStart, tempEnd);
            setOpen(false);
        }
    };

    const clearDates = () => {
        onApply('', '');
        setOpen(false);
    };

    // Format display label
    const displayLabel = () => {
        if (!hasDateFilter) return 'Periodo';
        const s = new Date(dateRange.start + 'T12:00:00');
        const e = new Date(dateRange.end + 'T12:00:00');
        const fmtShort = (d: Date) => `${d.getDate()}/${d.getMonth() + 1}`;
        if (dateRange.start === dateRange.end) return `Hoy`;
        return `${fmtShort(s)} – ${fmtShort(e)}`;
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 sm:py-2.5 rounded-xl border transition-all active:scale-95 ${hasDateFilter
                    ? 'text-white border-transparent'
                    : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                style={hasDateFilter ? { backgroundColor: primaryColor } : {}}
            >
                <CalendarDays className="w-4 h-4" />
                <span className="hidden sm:inline">{displayLabel()}</span>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-2 z-50 w-80 bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.15)] border border-slate-100 overflow-hidden"
                    >
                        {/* Presets */}
                        <div className="p-3 border-b border-slate-100">
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">Periodo rápido</p>
                            <div className="flex gap-1.5">
                                {presets.map(p => (
                                    <button
                                        key={p.label}
                                        onClick={() => applyPreset(p.start, p.end)}
                                        className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${dateRange.start === p.start && dateRange.end === p.end
                                            ? 'text-white'
                                            : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                                            }`}
                                        style={dateRange.start === p.start && dateRange.end === p.end ? { backgroundColor: primaryColor } : {}}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                                <button
                                    onClick={clearDates}
                                    className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${!hasDateFilter
                                        ? 'text-white'
                                        : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                                        }`}
                                    style={!hasDateFilter ? { backgroundColor: primaryColor } : {}}
                                >
                                    Todo
                                </button>
                            </div>
                        </div>

                        {/* Custom Range */}
                        <div className="p-3">
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2">Rango personalizado</p>
                            <div className="flex gap-2 items-center">
                                <div className="flex-1">
                                    <label className="text-[9px] font-bold text-slate-400 mb-1 block">Desde</label>
                                    <input
                                        type="date"
                                        value={tempStart}
                                        onChange={e => setTempStart(e.target.value)}
                                        className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 focus:outline-none focus:border-slate-300 bg-slate-50"
                                    />
                                </div>
                                <span className="text-slate-300 mt-4">—</span>
                                <div className="flex-1">
                                    <label className="text-[9px] font-bold text-slate-400 mb-1 block">Hasta</label>
                                    <input
                                        type="date"
                                        value={tempEnd}
                                        onChange={e => setTempEnd(e.target.value)}
                                        className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 focus:outline-none focus:border-slate-300 bg-slate-50"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={applyCustom}
                                disabled={!tempStart || !tempEnd}
                                className="w-full mt-2.5 py-2 text-xs font-bold text-white rounded-xl transition-all disabled:opacity-40 active:scale-[0.98]"
                                style={{ backgroundColor: primaryColor }}
                            >
                                Aplicar rango
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// Generic Column Filter Dropdown (Excel-style)
const ColumnFilterDropdown: React.FC<{
    label: string;
    items: string[];
    selected: Set<string>;
    onChange: (s: Set<string>) => void;
    primaryColor: string;
    displayMap?: Record<string, string>;
}> = ({ label, items, selected, onChange, primaryColor, displayMap }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = React.useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const getDisplayName = (item: string) => displayMap?.[item] || item;

    const filtered = items.filter(p =>
        getDisplayName(p).toLowerCase().includes(search.toLowerCase())
    );

    const allSelected = selected.size === items.length;
    const noneSelected = selected.size === 0;
    const isActive = !allSelected && !noneSelected;

    const toggle = (item: string) => {
        const next = new Set(selected);
        if (next.has(item)) next.delete(item);
        else next.add(item);
        onChange(next);
    };

    const selectAll = () => onChange(new Set(items));
    const clearAll = () => onChange(new Set());

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest py-1 px-1.5 rounded-lg transition-all ${isActive
                    ? 'text-indigo-600 bg-indigo-50'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                    }`}
            >
                {label}
                <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
                {isActive && (
                    <span className="w-4 h-4 rounded-full text-[8px] flex items-center justify-center text-white font-bold ml-0.5" style={{ backgroundColor: primaryColor }}>
                        {selected.size}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.97 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 top-full mt-1 z-50 w-64 bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] border border-slate-100 overflow-hidden"
                    >
                        {/* Search */}
                        {items.length > 4 && (
                            <div className="p-2.5 border-b border-slate-100">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300" />
                                    <input
                                        type="text"
                                        placeholder={`Buscar ${label.toLowerCase()}...`}
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg bg-slate-50 border border-slate-100 focus:outline-none focus:border-slate-200 focus:bg-white transition-colors placeholder:text-slate-300"
                                        autoFocus
                                    />
                                    {search && (
                                        <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500">
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-50">
                            <button onClick={selectAll} className="text-[9px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors">
                                Todo
                            </button>
                            <button onClick={clearAll} className="text-[9px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors">
                                Ninguno
                            </button>
                        </div>

                        {/* List */}
                        <div className="max-h-48 overflow-y-auto py-0.5">
                            {filtered.length === 0 ? (
                                <div className="px-3 py-4 text-center text-xs text-slate-300">Sin resultados</div>
                            ) : (
                                filtered.map(item => {
                                    const isSelected = selected.has(item);
                                    const displayName = getDisplayName(item);
                                    return (
                                        <button
                                            key={item}
                                            onClick={() => toggle(item)}
                                            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 transition-colors text-left group"
                                        >
                                            <div className={`w-4 h-4 rounded flex items-center justify-center transition-all flex-shrink-0 ${isSelected
                                                ? 'border-transparent'
                                                : 'border-2 border-slate-200 group-hover:border-slate-300'
                                                }`} style={isSelected ? { backgroundColor: primaryColor } : {}}>
                                                {isSelected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                                            </div>
                                            <span className={`text-xs truncate ${isSelected ? 'font-semibold text-slate-700' : 'text-slate-500'}`}>
                                                {displayName}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-3 py-2 bg-slate-50/80 border-t border-slate-100">
                            <p className="text-[9px] font-bold text-slate-400 text-center uppercase tracking-wider">
                                {selected.size}/{items.length}
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const config: Record<string, { label: string; bg: string; text: string; dot: string }> = {
        WAITING: { label: 'Registro', bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-400' },
        PREPARATION: { label: 'Preoperatorio', bg: 'bg-indigo-50', text: 'text-indigo-600', dot: 'bg-indigo-400' },
        ANESTHESIA: { label: 'Anestesia', bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-400' },
        SURGERY: { label: 'En Cirugía', bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-500' },
        RECOVERY: { label: 'Recuperación', bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
        EXIT: { label: 'Salida', bg: 'bg-slate-50', text: 'text-slate-500', dot: 'bg-slate-300' },
    };
    const c = config[status] || config.EXIT;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold ${c.bg} ${c.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${c.dot} ${status === 'SURGERY' ? 'animate-pulse' : ''}`}></span>
            {c.label}
        </span>
    );
};

export default HospitalDashboard;
